import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from "wouter";
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Search, Zap, FileText } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface Article {
  title: string;
  description: string;
  url: string;
  source: { name: string };
  publishedAt: string;
}

export default function SignalsPage() {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState('UK Fintech');
  const [submittedQuery, setSubmittedQuery] = useState('UK Fintech');

  const { data: articles, isLoading, error } = useQuery<Article[]>({ 
    queryKey: ['/api/signals', submittedQuery],
    queryFn: () => apiRequest<Article[]>('GET', `/api/signals?q=${submittedQuery}`),
    enabled: !!submittedQuery,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedQuery(query);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLocation("/")}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <span className="font-semibold text-lg">AcronIQ Veritus</span>
            </button>
            <div className="h-6 w-px bg-border mx-2" />
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Signal Tracker
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/reports")}
              className="text-muted-foreground hover:text-foreground"
            >
              <FileText className="h-4 w-4 mr-2" />
              Reports
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="text-muted-foreground hover:text-foreground"
            >
              Back to Chat
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-8">
        <div className="space-y-8 max-w-5xl mx-auto">
          <div className="space-y-2">
            <p className="text-muted-foreground">Monitor real-time market news and trends.</p>
          </div>

          <form onSubmit={handleSearch} className="flex gap-2">
            <Input 
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for topics like 'AI in healthcare' or 'Global supply chain'"
            />
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </form>

          {error && <p className="text-red-500">{(error as Error).message}</p>}

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {articles?.map((article, index) => (
              <a href={article.url} target="_blank" rel="noopener noreferrer" key={index}>
                <Card className="h-full hover:border-primary transition-colors">
                  <CardHeader>
                    <CardTitle className="text-lg">{article.title}</CardTitle>
                    <CardDescription>{article.source.name} - {new Date(article.publishedAt).toLocaleDateString()}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3">{article.description}</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
